import * as nodePath from 'node:path';
import { atom } from 'nanostores';
import type { ChangeQueue } from './change-queue';
import type { MigrationErrorData, MigrationsData, SupabaseChangeData, SupabaseStore } from './index';
import { diffFileMap, logger } from './utils';
import { applyMigration, getAllMigrations } from '~/lib/.client/api/supabase';
import { isCurrentChatUsingClaudeCode } from '~/lib/ai';
import { SUPABASE_DISCARDED_MIGRATIONS_FOLDER } from '~/lib/constants';
import { waitForProjectImport } from '~/lib/projects';
import { projectStore } from '~/lib/stores/project';
import { workbenchStore } from '~/lib/stores/workbench';
import { hmrPropertyFactory } from '~/utils/hmr';
import { invariant } from '~/utils/invariant';
import { waitUntil } from '~/utils/nanostore';
import { getRandomFileName } from '~/utils/randomName';
import { createDatabaseMigrationPath, extractQueryParts } from '~/utils/supabase';
import { removeWorkDir } from '~/utils/workDir';

const hmr = hmrPropertyFactory('SupabaseEdgeFunctionsStore');

/**
 * Global store holding all the state related to Supabase.
 */
export class SupabaseMigrationsStore {
  @hmr()
  applyingMigrations = atom(false);

  @hmr()
  #migrationsInDB = new Set<string>();

  @hmr()
  #migrationsDiscarded = new Set<string>();

  @hmr()
  #migrationIdsDiscarded = new Set<string>();

  constructor(
    private _supabaseStore: SupabaseStore,
    private _changesQueue: ChangeQueue,
  ) {}

  init() {
    this.#trackMigrationsFiles();

    this._supabaseStore.supabaseStatus.subscribe((status) => {
      if (status === 'CONFIGURED') {
        this.#supabaseProjectConnected(projectStore.getProjectId()!);
      }
    });

    this._supabaseStore.supabaseStatus.subscribe((status, previousStatus) => {
      if (status !== 'CONFIGURED' && previousStatus === 'CONFIGURED') {
        this._changesQueue.reset();
      }
    });
  }

  /**
   * For a given sql file path, return the name that the file should
   * have if it lives in `supabase/migrations`.
   *
   * @param filePath The current file path of the action.
   *
   * @returns Returns the file path for a migration.
   */
  getMigrationFilePath(filePath: string) {
    const migrations = new Set(Object.keys(workbenchStore.migrationFiles.get()));

    if (migrations.has(filePath)) {
      return filePath;
    }

    const existingFileNames = new Set(
      [...migrations].map((filePath) => {
        // grab the filename without extension and without numeric prefix
        return nodePath.basename(filePath, '.sql').slice(5);
      }),
    );

    let tries = 0;
    let randomFileName = getRandomFileName('_');

    while (existingFileNames.has(randomFileName) && tries < 25) {
      tries++;
      randomFileName = getRandomFileName('_');
    }

    return createDatabaseMigrationPath(randomFileName);
  }

  async acceptChange(changeId: string): Promise<{ change: SupabaseChangeData; success: boolean }> {
    const change = this._changesQueue.find(changeId);
    const projectId = projectStore.getProjectId();

    invariant(change);
    invariant(projectId);

    if (change.type === 'migration') {
      this._changesQueue.remove(changeId);

      this.applyingMigrations.set(true);

      let migrationError: MigrationErrorData | undefined;

      // we need to make sure migrations are applied in the right order
      const sortedMigrations = [...change.migrations].sort((a, b) => a.filePath.localeCompare(b.filePath));

      for (const migration of sortedMigrations) {
        const filePath = migration.filePath;
        const filename = nodePath.basename(filePath);

        const fileEntry = workbenchStore.files.get()[filePath];

        if (fileEntry?.type !== 'file') {
          logger.error(`Cannot apply migration because the file was removed or is no longer a file: '${filePath}'`);

          continue;
        }

        const { query } = extractQueryParts(fileEntry.content);

        try {
          await applyMigration(projectId, filename, query);

          this.#migrationsInDB.add(filename);
        } catch (error: any) {
          const message =
            error instanceof Error || 'message' in error
              ? error.message
              : 'Migration failed to apply because of an unknown error occurred';

          migrationError = {
            type: 'migration-error',
            id: crypto.randomUUID(),
            changeId,
            problems: [
              {
                type: 'supabase-migration',
                count: 1,
                id: crypto.randomUUID(),
                filePath,
                query,
                message,
              },
            ],
          };

          break;
        }
      }

      this.applyingMigrations.set(false);

      if (migrationError) {
        this._changesQueue.unshift(migrationError);
      }

      return { success: !migrationError, change };
    } else if (change.type === 'connect-required') {
      // temporarily disabled as per stopgap
    } else if (change.type === 'migration-error') {
      this._changesQueue.remove(changeId);

      try {
        // we remove the migration as it had an error
        await workbenchStore.removeFile(change.problems[0].filePath);
      } catch {
        // on error ignore
      }
    }

    return { success: true, change };
  }

  async discardChanges(changeId: string) {
    const change = this._changesQueue.find(changeId);

    invariant(change);

    if (change.type === 'migration') {
      this.#migrationIdsDiscarded.add(changeId);

      for (const migration of change.migrations) {
        this.#migrationsDiscarded.add(nodePath.basename(migration.filePath));
      }

      this._changesQueue.remove(changeId);

      for (const migration of change.migrations) {
        const filePath = migration.filePath;
        const filename = nodePath.basename(filePath);

        const newFilePath = `${SUPABASE_DISCARDED_MIGRATIONS_FOLDER}/${filename}`;

        await workbenchStore.createDirectory(removeWorkDir(SUPABASE_DISCARDED_MIGRATIONS_FOLDER));
        await workbenchStore.cut(removeWorkDir(filePath), removeWorkDir(newFilePath), 'file');
      }
    } else {
      this._changesQueue.remove(changeId);
    }
  }

  isDiscarded(changeId: string) {
    return this.#migrationIdsDiscarded.has(changeId);
  }

  async #supabaseProjectConnected(projectId: string) {
    if (this._changesQueue.migrationsInDBFetched.get()) {
      return;
    }

    invariant(this._supabaseStore.supabaseStatus.get() === 'CONFIGURED');

    // fetch all migrations
    for (const { filename } of await getAllMigrations(projectId)) {
      this.#migrationsInDB.add(filename);
    }

    // now that we fetched them let's update the queue
    const confirmationQueue = [];

    for (const change of this._changesQueue.getChangesInQueue()) {
      // discard those changes
      if (change.type === 'connect-required' || change.type === 'migration-error') {
        continue;
      }

      if (change.type === 'migration') {
        // only keep migrations that have not been applied or discarded
        change.migrations = change.migrations.filter((migration) => {
          const filename = nodePath.basename(migration.filePath);

          return !this.#migrationsInDB.has(filename) && !this.#migrationsDiscarded.has(filename);
        });

        if (change.migrations.length > 0) {
          confirmationQueue.push(change);
        }
      } else {
        // keep other unknown type of changes
        confirmationQueue.push(change);
      }
    }

    this._changesQueue.setState({ queue: confirmationQueue, migrationInDBFetched: true });
  }

  /**
   * Track migration files. Should be called at most once.
   */
  async #trackMigrationsFiles() {
    await waitForProjectImport();

    // wait until we've fetched the current status of the connection
    await waitUntil(this._supabaseStore.supabaseStatus, (value) => value !== 'UNKNOWN');

    return workbenchStore.migrationFiles.subscribe((migrationFiles, previousMigrationFiles) => {
      if (isCurrentChatUsingClaudeCode()) {
        // when claude code is enabled, we don't need to track the migrations files
        return;
      }

      const changesQueue = [...this._changesQueue.getChangesInQueue()];

      const changesAlreadyInQueue = new Set<string>(
        changesQueue.flatMap((change) => {
          if (change.type !== 'migration') {
            return [];
          }

          return change.migrations.map(({ filePath }) => filePath);
        }),
      );

      let hasNewOrUpdatedMigrations = false;

      let currentMigrationsChange = changesQueue.find((change) => change.type === 'migration');

      if (!currentMigrationsChange) {
        currentMigrationsChange = {
          type: 'migration',
          id: crypto.randomUUID(),
          migrations: [],
        };

        changesQueue.push(currentMigrationsChange);
      }

      const removedMigrations = new Set<string>(currentMigrationsChange.migrations.map(({ filePath }) => filePath));

      /**
       * Note, if the DB has migrations that we don't have locally, we just ignore
       * them. We should probably show a warning somewhere in the future but for now
       * we'll consider this an edge case that is fine to live with as the AI is not
       * fed the migrations but the schemas.
       */
      for (const filePath in migrationFiles) {
        removedMigrations.delete(filePath);

        const filename = nodePath.basename(filePath);
        const migrationFile = migrationFiles[filePath];

        if (
          this.#migrationsInDB.has(filename) ||
          this.#migrationsDiscarded.has(filename) ||
          migrationFile?.type !== 'file'
        ) {
          continue;
        }

        // the migration is not present in the DB, we need to get confirmation from the user before running them
        const { content } = migrationFile;
        const { summary, title } = extractQueryParts(content);

        const migrationsInQueue = currentMigrationsChange.migrations;

        let insertionIndex = -1;

        if (changesAlreadyInQueue.has(filePath)) {
          insertionIndex = migrationsInQueue.findIndex((migration) => migration.filePath === filePath);
        }

        const change = {
          filePath,
          longSummary: summary,
          shortSummary: title,
        } satisfies MigrationsData['migrations'][0];

        if (insertionIndex !== -1) {
          migrationsInQueue.splice(insertionIndex, 1, change);
        } else {
          migrationsInQueue.push(change);
        }

        hasNewOrUpdatedMigrations = true;
      }

      for (const removedMigration of removedMigrations) {
        const index = currentMigrationsChange.migrations.findIndex(
          (migration) => migration.filePath === removedMigration,
        );

        if (index !== -1) {
          currentMigrationsChange.migrations.splice(index, 1);
          hasNewOrUpdatedMigrations = true;
        }
      }

      // a new migration was added compared to what has been run in the backend
      if (hasNewOrUpdatedMigrations) {
        if (currentMigrationsChange.migrations.length === 0) {
          const index = changesQueue.indexOf(currentMigrationsChange);

          if (index !== -1) {
            changesQueue.splice(index, 1);
          }
        }

        this._changesQueue.setState({ queue: changesQueue });

        // if migrations have already been fetched we're done
        if (this._changesQueue.migrationsInDBFetched.get()) {
          return;
        }

        // this is the first call to subscribe we're also done
        if (!previousMigrationFiles) {
          return;
        }

        // otherwise we check if we should inject a 'connect-required'
        const { added } = diffFileMap(previousMigrationFiles, migrationFiles);
        const supabaseStatus = this._supabaseStore.supabaseStatus.get();

        if (added.length > 0 && supabaseStatus === 'NOT_CONFIGURED') {
          this._changesQueue.injectConnectRequired();
        }
      }
    });
  }
}
